/**
 * The dish rows of a comanda, grouped by wave.
 *
 * Two rules from `lib/order-card` carry the weight here. The row order never
 * depends on status: the cook presses these rows, and a row that re-sorts the
 * instant it is pressed puts the next press on the wrong dish. And a dish
 * ordered three times means three plates to make — drawn as three rows, or,
 * where the venue has asked for it, as one row saying three.
 *
 * Feature 252 reached the table board and left this view — the one the chef's
 * screen opens on — untouched, so a kitchen with the switch on saw nothing
 * change until someone pressed "Per tavolo". Grouping now happens here too,
 * off the same rule, so the two views cannot tell a cook different things.
 */
import React, { useState } from 'react';
import { formatTimer } from '@shared/utils/time';
import { useTranslation } from '../i18n';
import { useKitchenStore } from '../stores/kitchen.store';
import { useClientConfig } from '../hooks/useClientConfig';
import { stationLines, waveOf } from '../lib/order-card';
import { statusHint } from './GroupedDishRow';
import OrderCardItemRow from './OrderCardItemRow';

interface OrderCardItemsProps {
  items: any[];
  compact?: boolean;
  now: number;
  waveFiredAt: Map<number, Date | null>;
  /** Passed through to the wave row: in a station column it is the only timer. */
  isOverdue?: boolean;
  isWarning?: boolean;
  activeWave: number | null;
  loadingItemIds: Set<string>;
  /** Advances the line. The portions are the rows behind it, one request each. */
  onAdvance: (item: any, portions?: any[]) => void;
  onOpenDish: (item: any) => void;
  /**
   * Feature 252 — override for tests. Left alone, the switch is read from the
   * venue configuration, which is refreshed every few seconds: flipping it
   * redraws open comandas without anyone restarting anything.
   */
  groupIdenticalDishes?: boolean;
}

function WaveDivider({
  wave,
  firedAt,
  isActive,
  now,
  isOverdue,
  isWarning,
}: {
  wave: number;
  firedAt: Date | null;
  isActive: boolean;
  now: number;
  /** The colours the card header used to carry. In a station column this row
   *  is the ONLY timer, so dropping them there would have dropped "late". */
  isOverdue?: boolean;
  isWarning?: boolean;
}) {
  const { t } = useTranslation();
  const secs = isActive && firedAt ? Math.floor((now - firedAt.getTime()) / 1000) : null;
  const done = firedAt !== null && !isActive;

  return (
    <div className="flex items-center gap-3 py-1">
      <div className="h-px flex-1 bg-kds-muted/30" />
      <span className="text-kds-badge text-kds-muted">{t('kds.wave.label', 'Ondata {{wave}}', { wave })}</span>
      {secs !== null ? (
        <span
          className={[
            'text-kds-badge font-mono',
            // The same two the card header uses, so "late" reads the same
            // whichever timer a cook happens to be looking at.
            isOverdue
              ? 'text-kds-cancelled font-bold'
              : isWarning
                ? 'text-kds-preparing font-bold'
                : 'text-kds-preparing',
          ].join(' ')}
        >
          {formatTimer(secs)}
        </span>
      ) : done ? (
        <span className="text-kds-badge uppercase tracking-wider text-kds-ready">{t('kds.wave.ready', 'Pronta')}</span>
      ) : (
        <span className="text-kds-badge uppercase tracking-wider text-kds-muted italic">
          {t('kds.dishRow.waiting', 'In attesa')}
        </span>
      )}
      <div className="h-px flex-1 bg-kds-muted/30" />
    </div>
  );
}

export default function OrderCardItems({
  items,
  compact,
  now,
  waveFiredAt,
  isOverdue,
  isWarning,
  activeWave,
  loadingItemIds,
  onAdvance,
  onOpenDish,
  groupIdenticalDishes,
}: OrderCardItemsProps) {
  const { t } = useTranslation();
  const clientId = useKitchenStore((st: any) => st.clientId);
  const { clientConfig } = useClientConfig(clientId);
  // Read on every render, never captured in state: that is what makes the
  // switch reach a comanda that is already on screen.
  const grouping = groupIdenticalDishes ?? clientConfig?.groupIdenticalDishes === true;
  // Feature 268 — read the same way and for the same reason: flipping the
  // switch must reach a comanda that is already on the screen.
  const showDinerTag = clientConfig?.dinerTagOnServiceEnabled === true;

  // Per screen and per visit. A cook opening a line to check something must not
  // change what every other screen in the kitchen shows.
  const [openLines, setOpenLines] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpenLines((open) => {
      const next = new Set(open);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const lines = stationLines(items, grouping);

  return (
    <div className="space-y-1.5">
      {lines.map((line, index) => {
        const wave = waveOf(line.item);
        const previousWave = index > 0 ? waveOf(lines[index - 1].item) : 0;
        const isLoading = line.portions.some((p: any) => loadingItemIds.has(p.id));
        // Only a line made of several DATABASE rows has anything to show when
        // opened: one row sent as "three of these" is one row underneath.
        const openable = line.portions.length > 1;
        const isOpen = openable && openLines.has(line.key);

        return (
          <React.Fragment key={line.key}>
            {wave !== previousWave && (
              <WaveDivider
                wave={wave}
                firedAt={waveFiredAt.get(wave) ?? null}
                isActive={activeWave === wave}
                isOverdue={isOverdue}
                isWarning={isWarning}
                now={now}
              />
            )}

            <OrderCardItemRow
              item={line.item}
              compact={compact}
              isLoading={isLoading}
              onAdvance={() => onAdvance(line.item, line.portions)}
              onOpenDish={onOpenDish}
              count={line.count}
              statusHint={statusHint(line.counts, line.count, t)}
              expanded={isOpen}
              onToggleExpanded={openable ? () => toggle(line.key) : undefined}
              showDinerTag={showDinerTag}
            />

            {isOpen && (
              <div className="pl-3 border-l-2 border-kds-muted/30 space-y-1.5">
                {line.portions.map((portion: any) => (
                  <OrderCardItemRow
                    key={portion.id}
                    item={portion}
                    compact={compact}
                    isLoading={loadingItemIds.has(portion.id)}
                    onAdvance={() => onAdvance(portion)}
                    onOpenDish={onOpenDish}
                    showDinerTag={showDinerTag}
                  />
                ))}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
